import { QuestionPage, PageElement, PageConfig } from "../types/types";
import { QuestionDetails } from "../../../../../../hooks/questions/types/types";

/**
 * Configuration interface for page calculations
 */
interface PageCalculatorConfig {
  A4_WIDTH: number;
  A4_HEIGHT: number;
  effectiveWidth: number;
  effectiveHeight: number;
  fontSizeQuestoes: number;
  fontSizeAlternatives: number;
  marginSuperior: number;
  marginInferior: number;
  marginEsquerda: number;
  marginDireita: number;
  numeroColunas: number;
  randomAlternatives: boolean;
  randomQuestion: boolean;
  editMode?: boolean; // Novo campo para controlar adição de elementos em modo de edição
  printMode?: boolean; // Modo de impressão

  // Parâmetros opcionais para o QR Code:
  studentId?: string;
  registrationId?: string;
  testId?: string;
  answerSheetId?: string;
  diagramId?: string;
  urltoken?: string; // Novo parâmetro para URL dinâmica
  edit?: boolean;    // Para controlar modo de edição
}

// Constants
const COLUMN_GAP = 20;
const QUESTION_MARGIN = 15;
const QR_CODE_HEIGHT = 100;
const QR_CODE_MARGIN_BOTTOM = 10;
const SEPARATOR_HEIGHT = 2;
const TOTAL_RESERVED_HEIGHT =
  QR_CODE_HEIGHT + QR_CODE_MARGIN_BOTTOM + SEPARATOR_HEIGHT;
// Defina como constante global no topo do arquivo
const ANSWER_AREA_MIN_HEIGHT = 300; // ou 400, escolha um valor único

// Interface para armazenar o id da questão e seu índice global
interface DissertativeId {
  id: string;
  globalIndex: number;
}

class ContentManager {
  pages: QuestionPage[] = [];
  currentPage: any; // sem tipos estritos
  currentColumn = 0;
  remainingColumnHeights: number[] = [];
  currentPageColumns: number;
  // IDs das questões dissertativas (para gerar o QR Code) – armazenados com índice global
  private currentPageQuestionIds: DissertativeId[] = [];
  private hasDissertative: boolean = false;
  private config: PageCalculatorConfig;
  private pageConfigs: Record<number, PageConfig>;
  private pageColumns: number[];
  private tempDivCache: HTMLDivElement | null = null;
  private columnCurrentYPositions: number[] = [];
  // Flag para identificar questões dissertativas grandes
  private hasHugeDissertative: boolean = false;
  // Mapeamento global para numeração progressiva dos IDs dissertativos
  private globalQuestionMapping: Record<string, number> = {};
  private globalQuestionCounter: number = 1;

  constructor(
    config: PageCalculatorConfig,
    pageConfigs: Record<number, PageConfig> = {},
    pageColumns: number[] = [],
    initialColumns?: number
  ) {
    this.config = config;
    this.pageConfigs = pageConfigs;
    this.pageColumns = pageColumns;
    this.currentPageColumns =
      (pageConfigs && pageConfigs[0]?.columns) ||
      initialColumns ||
      config.numeroColunas;

    this.columnCurrentYPositions = Array(this.currentPageColumns).fill(
      config.marginSuperior
    );
    // A página inicia utilizando toda a altura disponível
    this.currentPage = { elements: [], columns: this.currentPageColumns };
    this.remainingColumnHeights = Array(this.currentPageColumns).fill(
      config.effectiveHeight
    );
    this.currentPageQuestionIds = [];

    // Certifique-se de que o contador global comece em 1
    this.globalQuestionCounter = 1;
    this.globalQuestionMapping = {};
  }

  private createTemporaryDiv(): HTMLDivElement {
    if (this.tempDivCache) {
      document.body.appendChild(this.tempDivCache);
      return this.tempDivCache;
    }
    const div = document.createElement("div");
    div.style.width = `${this.getColumnWidth(this.currentPageColumns)}px`;
    div.style.visibility = "hidden";
    document.body.appendChild(div);
    this.tempDivCache = div;
    return div;
  }

  private createSeparatorString(): string {
    return `<div data-element-type="separator" id="separator" style="position:absolute; width:100%; height:${SEPARATOR_HEIGHT}px; background-color:#000; z-index:10;"></div>`;
  }

  cleanup() {
    if (this.tempDivCache && this.tempDivCache.parentNode) {
      document.body.removeChild(this.tempDivCache);
    }
  }

  private getColumnWidth(columns: number): number {
    return columns === 1
      ? this.config.effectiveWidth
      : (this.config.effectiveWidth - COLUMN_GAP * (columns - 1)) / columns;
  }

  /**
   * Mede o conteúdo HTML (já corrigido) e retorna sua altura acrescida de QUESTION_MARGIN.
   */
  measureContent(
    content: string,
    fontSize: number,
    columnWidth?: number
  ): number {
    const tempDiv = this.createTemporaryDiv();
    tempDiv.style.fontSize = `${fontSize}px`;
    if (columnWidth) tempDiv.style.width = `${columnWidth}px`;
    tempDiv.innerHTML = this.fixHtmlBreak(this.sanitizeHTML(content));
    const height = tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);
    return height + QUESTION_MARGIN;
  }

  /**
   * Sanitiza o HTML, ajustando as tags de <img> para garantir responsividade.
   */
  private sanitizeHTML(html: string): string {
    let processed = html.replace(/<img([^>]*?)>/gi, (match, group1) => {
      const srcMatch = group1.match(/src=["']([^"']*)["']/i);
      const srcValue = srcMatch ? srcMatch[1] : "";
      if (!srcValue.trim()) return "";
      return `<img${group1} style="max-width:100%; height:auto;">`;
    });
    processed = processed.replace(
      /<(?!\/|br|hr|img|input|link|meta|area|base|col|embed|keygen|param|source|track|wbr)([a-z][a-z0-9]*)[^>]*>/gi,
      (match) => {
        if (match.endsWith("/>")) return match;
        if (!match.includes("</")) return match;
        return match;
      }
    );
    return processed;
  }

  /**
   * Corrige quebras em tags HTML que possam ter ocorrido por quebra de conteúdo.
   * Remove tag incompleta no final e fecha tags abertas.
   */
  private fixHtmlBreak(content: string): string {
    const lastOpenIndex = content.lastIndexOf("<");
    const lastCloseIndex = content.lastIndexOf(">");
    if (lastOpenIndex > lastCloseIndex) {
      content = content.substring(0, lastOpenIndex);
    }
    const tagStack: string[] = [];
    const regex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(content)) !== null) {
      const fullTag = match[0];
      const tagName = match[1];
      if (fullTag.startsWith("</")) {
        if (tagStack.length > 0 && tagStack[tagStack.length - 1] === tagName) {
          tagStack.pop();
        }
      } else if (!fullTag.endsWith("/>")) {
        tagStack.push(tagName);
      }
    }
    while (tagStack.length > 0) {
      const tagName = tagStack.pop();
      content += `</${tagName}>`;
    }
    return content;
  }

  /**
   * Cria um elemento combinado para questões dissertativas que não devem ser quebradas.
   */
  private createCombinedDissertativeElement(
    content: string,
    fontSize: number
  ): string {
    return `<div class="dissertative-combined" style="font-size: ${fontSize}px;">
              ${this.fixHtmlBreak(content)}
            </div>`;
  }

  /**

  /**
   * Retorna a altura disponível na coluna atual.
   */
  private getAvailableHeight(): number {
    return (
      this.config.effectiveHeight -
      (this.columnCurrentYPositions[this.currentColumn] -
        this.config.marginSuperior)
    );
  }

  /**
   * Processa uma questão dissertativa dividindo o conteúdo conforme o espaço disponível.
   * Durante o processamento, se for necessário criar uma nova página por falta de espaço,
   * chamamos createNewPage(true) para propagar o ID da questão.
   * Ao final, se a questão NÃO foi dividida (ou seja, se todo o conteúdo coube em uma única página),
   * removemos o ID para que ele não seja propagado para páginas futuras.
   */
  private addDissertativeElement(
    content: string,
    fontSize: number,
    questionId?: string
  ): void {
    const columnWidth = this.getColumnWidth(this.currentPageColumns);
    let remainingContent = content;
    let wasSplit = false;

    // Pré-verifica se o conteúdo incluiria uma área de resposta
    const wouldIncludeAnswerArea =
      questionId && content.includes("dissertative");

    while (remainingContent.trim().length > 0) {
      const availableHeight = this.getAvailableHeight();

      // Se seria uma questão com área de resposta mas não há espaço suficiente,
      // força uma nova página imediatamente
      if (
        wouldIncludeAnswerArea &&
        availableHeight < ANSWER_AREA_MIN_HEIGHT + 150
      ) {
        if (!this.moveToNextColumn()) {
          this.createNewPage(true);
          wasSplit = true;
        }
        continue;
      }

      // Busca o melhor ponto de quebra
      const { fit, remaining } = this.findBestBreakPoint(
        remainingContent,
        fontSize,
        availableHeight,
        columnWidth
      );

      if (!fit) {
        console.warn("Não foi possível encaixar conteúdo na página atual");
        if (!this.moveToNextColumn()) {
          this.createNewPage(true);
          wasSplit = true;
        }
        continue;
      }

      // Cria o elemento com o conteúdo que encaixa
      const fitElement = this.createCombinedDissertativeElement(fit, fontSize);
      const fitHeight = this.measureContent(fitElement, fontSize, columnWidth);
      const pos = this.calculateElementPosition(fitHeight);

      // Adiciona o elemento à página atual
      this.currentPage.elements.push({
        element: fitElement,
        x: pos.x,
        y: pos.y,
        width: columnWidth,
        height: fitHeight,
      });

      // Após adicionar o elemento, verifica se a área de resposta foi implementada corretamente
      if (questionId && fit.includes('data-element-type="dissertative"')) {
        const elementAdded =
          this.currentPage.elements[this.currentPage.elements.length - 1];
        const hasCorrectAnswerArea =
          elementAdded.element.includes("pentagon-answer-box") &&
          elementAdded.element.includes("cornerMark") &&
          elementAdded.element.includes(`data-question-id="${questionId}"`);

        // Se o elemento foi adicionado mas a área de resposta não está correta, move para a próxima página
        if (!hasCorrectAnswerArea && wouldIncludeAnswerArea) {
          // Remove o elemento adicionado incorretamente
          this.currentPage.elements.pop();

          if (!this.moveToNextColumn()) {
            this.createNewPage(true);
            wasSplit = true;
          }
          continue;
        }
      }

      // Atualiza o conteúdo restante
      remainingContent = remaining;

      // Se ainda há conteúdo e espaço insuficiente, força nova página
      if (
        remainingContent.trim().length > 0 &&
        this.getAvailableHeight() < fontSize * 3
      ) {
        if (!this.moveToNextColumn()) {
          this.createNewPage(true);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          wasSplit = true;
        }
      }
    }

    // Mantenha sempre a flag hasDissertative se houver algum ID
    if (questionId) {
      this.hasDissertative = this.currentPageQuestionIds.length > 0;
    }
  }

  /**
   * Coleta todos os IDs de questões que têm áreas de resposta implementadas na página atual
   */
  private collectImplementedQuestionIds(): string[] {
    const implementedIds: string[] = [];

    // Verifica todos os elementos da página atual
    this.currentPage.elements.forEach((element: any) => {
      const elementHtml = element.element || "";

      // Procura por áreas de resposta com data-question-id
      const questionIdMatch = elementHtml.match(/data-question-id="([^"]+)"/);

      if (
        questionIdMatch &&
        (elementHtml.includes('class="answer-area pentagon-answer-box"') ||
          (elementHtml.includes("answer-area") &&
            elementHtml.includes("pentagon-answer-box"))) &&
        elementHtml.includes('data-element-type="dissertative"')
      ) {
        const questionId = questionIdMatch[1];
        if (!implementedIds.includes(questionId)) {
          implementedIds.push(questionId);
        }
      }
    });


    return implementedIds;
  }

  private getImplementedQuestionIds(): DissertativeId[] {
    // Modifica para não depender apenas da flag hasDissertative
    if (this.collectImplementedQuestionIds().length === 0) {

      return [];
    }



    // Coleta todos os IDs de questões que têm áreas de resposta implementadas
    const implementedIdStrings = this.collectImplementedQuestionIds();

    // Se não encontrou nenhuma área implementada, retorna array vazio
    if (implementedIdStrings.length === 0) {

    }

    // Converte os IDs de string para objetos DissertativeId
    const implementedIds: DissertativeId[] = [];

    implementedIdStrings.forEach((id) => {
      // Verifica se o ID está no mapeamento global
      if (!this.globalQuestionMapping[id]) {
        // Registra o ID com o próximo número da sequência
        this.globalQuestionMapping[id] = this.globalQuestionCounter;

        // Incrementa o contador para a próxima questão
        this.globalQuestionCounter++;


      } else {

      }

      // Adiciona ao array com o índice correto
      implementedIds.push({
        id: id,
        globalIndex: this.globalQuestionMapping[id],
      });
    });

    

    return implementedIds;
  }

  // Modificar o método ensureQRCode para apenas reservar espaço
  private ensureQRCode(): void {
    // Apenas reserva o espaço, mas não adiciona o QR code físico
    this.ensureBelowQrLine();
  }

  /**
   * Insere um elemento na página.
   *
   * Para elementos do tipo "dissertative", sempre registra o ID (se questionId existir)
   * independentemente do conteúdo. Assim, se a questão for dividida, ambas as partes terão o mesmo ID.
   */
  addElement(
    content: string,
    fontSize: number,
    elementType: string = "question",
    questionId?: string
  ) {
    // Verifica se o conteúdo já está formatado com divs e classes
    const isAlreadyFormatted =
      content.includes('class="question-content"') ||
      content.includes("data-element-type");

    if (isAlreadyFormatted && (this.config.editMode || this.config.printMode)) {
      // Para elementos já formatados em modo de edição ou impressão, processa diretamente sem wrappers adicionais
      const columnWidth = this.getColumnWidth(this.currentPageColumns);
      const height = this.measureContent(content, fontSize, columnWidth);
      const pos = this.calculateElementPosition(height);

      this.currentPage.elements.push({
        element: content, // Usa o conteúdo como está
        x: pos.x,
        y: pos.y,
        width: columnWidth,
        height: height,
      });

      // Ainda registra IDs se for elemento dissertative
      if (elementType === "dissertative" && questionId) {
        // Código para registrar IDs (mantendo o existente)
      }

      return;
    }

    // Código original para elementos novos ou não formatados
    // Se for uma questão dissertativa, verificar se há espaço para área de resposta
    if (elementType === "dissertative" && questionId) {
      const maxDissertativeHeight =
        this.config.effectiveHeight - TOTAL_RESERVED_HEIGHT;
      const answerAreaMinHeight = 400;

      if (maxDissertativeHeight >= answerAreaMinHeight) {
        if (!this.currentPageQuestionIds.some((q) => q.id === questionId)) {
          if (!this.globalQuestionMapping[questionId]) {
            this.globalQuestionMapping[questionId] = this.globalQuestionCounter;
            this.globalQuestionCounter++;
          }
          this.currentPageQuestionIds.push({
            id: questionId,
            globalIndex: this.globalQuestionMapping[questionId],
          });

          if (!this.hasDissertative) {
            this.hasDissertative = true;
            const reservedLine =
              this.config.marginSuperior + TOTAL_RESERVED_HEIGHT;
            for (let i = 0; i < this.columnCurrentYPositions.length; i++) {
              if (this.columnCurrentYPositions[i] < reservedLine) {
                this.columnCurrentYPositions[i] = reservedLine;
              }
            }
          }
        }
      }
    }

    const columnWidth = this.getColumnWidth(this.currentPageColumns);

    if (elementType === "dissertative") {
      this.addDissertativeElement(content, fontSize, questionId);
      return;
    }

    // Lógica padrão para outros tipos de elemento...
    const fixedContent = this.fixHtmlBreak(content);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const wrappedContent = `<div style="font-size: ${fontSize}px" class="question-content" data-element-type="${elementType}">${fixedContent}</div>`;
    let remainingContent = content;
    while (remainingContent) {
      const availableHeightSegment =
        this.config.effectiveHeight -
        (this.columnCurrentYPositions[this.currentColumn] -
          this.config.marginSuperior);
      const { fit, remaining } = this.findBestBreakPoint(
        remainingContent,
        fontSize,
        availableHeightSegment,
        columnWidth
      );
      if (fit) {
        const partWrappedContent = `<div style="font-size: ${fontSize}px" class="question-content" data-element-type="${elementType}">${this.fixHtmlBreak(
          fit
        )}</div>`;
        const partHeight = this.measureContent(
          partWrappedContent,
          fontSize,
          columnWidth
        );
        const pos = this.calculateElementPosition(partHeight);
        this.currentPage.elements.push({
          element: partWrappedContent,
          x: pos.x,
          y: pos.y,
          width: columnWidth,
          height: partHeight,
        });
        remainingContent = remaining;
      }
      if (remaining && (!fit || remaining !== remainingContent)) {
        if (!this.moveToNextColumn()) {
          this.createNewPage();
          if (elementType === "dissertative") {
            this.ensureBelowQrLine();
          }
        }
      } else if (!remaining) {
        break;
      }
    }
  }

  // Modificar getPages para não tentar adicionar QR codes
  getPages(): QuestionPage[] {

    // Verifica se há áreas de resposta de questões dissertativas na página atual
    const hasAnyDissertativeArea = this.currentPage.elements.some(
      (element: any) =>
        element.element &&
        element.element.includes('data-element-type="dissertative"') &&
        (element.element.includes(
          'class="answer-area pentagon-answer-box"'
        ) ||
          (element.element.includes("answer-area") &&
            element.element.includes("pentagon-answer-box")))
    );

    // Apenas reserva o espaço se houver elementos dissertativos
    if (this.hasDissertative || hasAnyDissertativeArea) {
      this.ensureBelowQrLine();
    }

    // Finaliza a página atual, se houver conteúdo ou IDs registrados
    if (
      this.currentPage.elements.length > 0 ||
      this.currentPageQuestionIds.length > 0
    ) {
      this.pages.push(this.currentPage);
    } else {
    }
    this.cleanup();
    return this.pages;
  }

  /**
   * Garante que a posição atual da coluna esteja abaixo da linha reservada (ex.: QR Code).
   */
  private ensureBelowQrLine(): void {
    // Sempre reserva espaço para elementos dissertativos, independente da flag hasDissertative
    const reservedLine = this.config.marginSuperior + TOTAL_RESERVED_HEIGHT;
    for (let i = 0; i < this.columnCurrentYPositions.length; i++) {
      if (this.columnCurrentYPositions[i] < reservedLine) {
        this.columnCurrentYPositions[i] = reservedLine;
      }
    }
  }

  /**
   * Cria uma nova página.
   *
   * Se a página atual contém conteúdo dissertativo com IDs registrados, gera o QR Code
   * usando esses IDs. Ao finalizar a página, se a criação for por continuação (por falta de espaço),
   * propagamos os IDs para a nova página; caso contrário, limpamos.
   */
  private createNewPage(isDissertativeContinuation: boolean = false) {
    // Verifica se há áreas de resposta dissertativas na página atual
    const hasAnyDissertativeArea = this.currentPage.elements.some(
      (element: any) =>
        element.element &&
        element.element.includes('data-element-type="dissertative"') &&
        (element.element.includes(
          'class="answer-area pentagon-answer-box"'
        ) ||
          (element.element.includes("answer-area") &&
            element.element.includes("pentagon-answer-box")))
    );

    // Adiciona metadados sobre questões dissertativas, mas NÃO adiciona o QR code físico
    if (
      (this.hasDissertative && this.currentPageQuestionIds.length > 0) ||
      hasAnyDissertativeArea
    ) {
      // Use apenas os IDs de questões com áreas de resposta implementadas
      const implementedQuestionIds = this.getImplementedQuestionIds();

      if (implementedQuestionIds.length > 0) {
        const sortedQuestionIds = implementedQuestionIds
          .slice()
          .sort((a, b) => a.globalIndex - b.globalIndex);

        // Adiciona atributos data- à página para posterior geração dinâmica do QR code
        this.currentPage.dissertativeData = {
          questionIds: sortedQuestionIds,
          pageIndex: this.pages.length + 1
        };
        
        // Adiciona apenas o separador, não o QR code
        const separatorElement: PageElement = {
          element: this.createSeparatorString(),
          x: this.config.marginEsquerda,
          y: this.config.marginSuperior + QR_CODE_HEIGHT,
          width: this.config.effectiveWidth,
          height: SEPARATOR_HEIGHT,
        };
        
        this.currentPage.elements.splice(0, 0, separatorElement);
        
        // Reserva o espaço onde o QR code seria posicionado
        this.ensureBelowQrLine();
      }
    }

    // Finaliza a página atual
    this.pages.push(this.currentPage);

    // Se a criação for por continuação, propagamos os IDs; caso contrário, limpamos.
    const newQuestionIds = isDissertativeContinuation
      ? [...this.currentPageQuestionIds]
      : [];
    this.currentPage = { elements: [], columns: this.currentPageColumns };
    this.currentPageQuestionIds = newQuestionIds;
    this.hasDissertative = newQuestionIds.length > 0;
    this.hasHugeDissertative = false;

    const initialY = this.config.marginSuperior;
    this.columnCurrentYPositions = Array(this.currentPageColumns).fill(
      initialY
    );
    this.remainingColumnHeights = Array(this.currentPageColumns).fill(
      this.config.effectiveHeight
    );
    this.currentColumn = 0;
  }

  /**
   * Calcula a posição para o novo elemento com base na coluna atual e atualiza a posição vertical.
   */
  calculateElementPosition(
    height: number,
    isFixed: boolean = false
  ): { x: number; y: number } {
    if (isFixed) {
      // Retorna uma posição predefinida para elementos fixos
      return {
        x: this.config.marginEsquerda,
        y: this.config.marginInferior,
      };
    }
    const columnWidth = this.getColumnWidth(this.currentPageColumns);

    // Usa a verificação mais abrangente para garantir que todos os elementos fiquem abaixo da linha reservada
    if (
      this.hasAnyDissertativeElements() &&
      this.columnCurrentYPositions[this.currentColumn] <
        this.config.marginSuperior + TOTAL_RESERVED_HEIGHT
    ) {
      this.columnCurrentYPositions[this.currentColumn] =
        this.config.marginSuperior + TOTAL_RESERVED_HEIGHT;
    }

    const yPosition = this.columnCurrentYPositions[this.currentColumn];
    this.columnCurrentYPositions[this.currentColumn] += height;
    return {
      x:
        this.config.marginEsquerda +
        (columnWidth + COLUMN_GAP) * this.currentColumn,
      y: yPosition,
    };
  }

  /**
   * moveToNextColumn - Verifica se há próxima coluna para utilizar.
   */
  private moveToNextColumn(): boolean {
    if (this.currentColumn < this.currentPageColumns - 1) {
      this.currentColumn++;
      return true;
    }
    return false;
  }

  /**
   * Função auxiliar para ajustar a quebra do conteúdo e evitar que uma tag HTML seja cortada.
   */
  private adjustHtmlBreak(content: string): string {
    const lastOpenIndex = content.lastIndexOf("<");
    const lastCloseIndex = content.lastIndexOf(">");
    if (lastOpenIndex > lastCloseIndex) {
      return content.substring(0, lastOpenIndex);
    }
    return content;
  }

  /**
   * Determina o melhor ponto de quebra para o conteúdo.
   */
  private findBestBreakPoint(
    content: string,
    fontSize: number,
    maxHeight: number,
    columnWidth: number
  ): { fit: string; remaining: string } {
    // Ignora elementos fixos
    if (content.includes('data-fixed="true"')) {
      return { fit: content, remaining: "" };
    }
    if (!content.trim()) return { fit: "", remaining: "" };
    const tempDiv = this.createTemporaryDiv();
    tempDiv.style.fontSize = `${fontSize}px`;
    tempDiv.style.width = `${columnWidth}px`;
    tempDiv.innerHTML = this.fixHtmlBreak(this.sanitizeHTML(content));

    if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
      document.body.removeChild(tempDiv);
      return { fit: content, remaining: "" };
    }

    const paragraphs = content.split("\n");
    let fitContent = "";
    let remainingContent = "";
    let low = 0;
    let high = paragraphs.length - 1;
    let mid = Math.floor((high + low) / 2);
    let lastFitIndex = -1;

    while (low <= high) {
      const testContent = paragraphs.slice(0, mid + 1).join("\n");
      tempDiv.innerHTML = this.fixHtmlBreak(this.sanitizeHTML(testContent));
      if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
        lastFitIndex = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
      mid = Math.floor((high + low) / 2);
    }

    if (lastFitIndex >= 0) {
      fitContent = paragraphs.slice(0, lastFitIndex + 1).join("\n");
      remainingContent = paragraphs.slice(lastFitIndex + 1).join("\n");
    } else {
      const firstParagraph = paragraphs[0];
      const sentences = firstParagraph.split(". ");
      for (let j = 0; j < sentences.length; j++) {
        const testSentence = sentences.slice(0, j + 1).join(". ");
        tempDiv.innerHTML = this.fixHtmlBreak(this.sanitizeHTML(testSentence));
        if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
          fitContent = testSentence;
          const remainingSentences = sentences.slice(j + 1).join(". ");
          const remainingParagraphs = paragraphs.slice(1).join("\n");
          remainingContent =
            remainingSentences +
            (remainingSentences && remainingParagraphs ? ". " : "") +
            (remainingParagraphs ? "\n" + remainingParagraphs : "");
        } else if (j === 0) {
          const words = sentences[0].split(" ");
          let wordIndex = 0;
          while (wordIndex < words.length) {
            const testWords = words.slice(0, wordIndex + 1).join(" ");
            tempDiv.innerHTML = this.fixHtmlBreak(this.sanitizeHTML(testWords));
            if (tempDiv.offsetHeight + QUESTION_MARGIN <= maxHeight) {
              fitContent = testWords;
              wordIndex++;
            } else {
              break;
            }
          }
          const remainingWords = words.slice(wordIndex).join(" ");
          const remainingSentences = sentences.slice(1).join(". ");
          const remainingParagraphs = paragraphs.slice(1).join("\n");
          remainingContent =
            remainingWords +
            (remainingWords && remainingSentences ? " " : "") +
            (remainingSentences ? ". " + remainingSentences : "") +
            ((remainingWords || remainingSentences) && remainingParagraphs
              ? "\n"
              : "") +
            (remainingParagraphs || "");
          break;
        } else {
          break;
        }
      }
    }

    const adjustedFit = this.adjustHtmlBreak(fitContent);
    if (adjustedFit.length < fitContent.length) {
      remainingContent =
        fitContent.substring(adjustedFit.length) + remainingContent;
      fitContent = adjustedFit;
    }

    document.body.removeChild(tempDiv);
    return { fit: fitContent, remaining: remainingContent.trim() };
  }

  /**
   * Verifica se a página contém elementos dissertativos, independentemente da flag
   */
  private hasAnyDissertativeElements(): boolean {
    return (
      this.hasDissertative ||
      this.currentPage.elements.some(
        (element: any) =>
          element.element &&
          (element.element.includes('data-element-type="dissertative"') ||
            (element.element.includes("answer-area") &&
              element.element.includes("pentagon-answer-box")))
      )
    );
  }
}

/**
 * Função para embaralhar um array
 */
function shuffleArray<T>(array: T[]): T[] {
  if (!array || array.length <= 1) return array;
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Processa as tags de imagem no HTML
 */
function processImgHTML(html: string): string {
  if (!html) return "";
  return html.replace(/<img([^>]*?)>/gi, (match, group1) => {
    const srcMatch = group1.match(/src=["']([^"']+)["']/i);
    const srcValue = srcMatch ? srcMatch[1] : "";
    return srcValue
      ? `<img${group1} style="max-width:100%; height:auto;">`
      : "";
  });
}
export function getElementType(html: string): string {
  if (!html) return "question";
  const dataTypeMatch = html.match(/data-element-type=["']([^"']+)["']/i);
  return dataTypeMatch ? dataTypeMatch[1] : "question";
}

/**
 * Remove a div externa se presente
 */
export function stripOuterDiv(html: string): string {
  if (!html) return "";
  const regex = /^<div[^>]*>(.*)<\/div>$/i;
  const match = html.match(regex);
  return match ? match[1] : html;
}

/**
 * Função auxiliar para dividir responsivamente o enunciado em blocos
 */
function splitTextIntoBlocks(
  text: string,
  fontSize: number,
  maxHeight: number,
  containerWidth: string = "calc(100% - 20px)"
): string[] {
  const tempDiv = document.createElement("div");
  tempDiv.style.fontSize = `${fontSize}px`;
  tempDiv.style.width = containerWidth;
  tempDiv.style.position = "absolute";
  tempDiv.style.visibility = "hidden";
  document.body.appendChild(tempDiv);

  const parts: string[] = [];
  let remainingText = text.trim();

  while (remainingText.length > 0) {
    tempDiv.innerHTML = remainingText;
    if (tempDiv.offsetHeight <= maxHeight) {
      parts.push(remainingText);
      break;
    }

    let low = 0;
    let high = remainingText.length;
    let best = 0;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = remainingText.substring(0, mid);
      tempDiv.innerHTML = candidate;
      if (tempDiv.offsetHeight <= maxHeight) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    let splitIndex = remainingText.lastIndexOf(" ", best);
    if (splitIndex === -1 || splitIndex < best / 2) {
      splitIndex = best;
    }
    let part = remainingText.substring(0, splitIndex).trim();
    if (!part) {
      part = remainingText.substring(0, best).trim();
      splitIndex = best;
    }
    parts.push(part);
    remainingText = remainingText.substring(splitIndex).trim();
  }

  document.body.removeChild(tempDiv);
  return parts;
}

/**
 * Normaliza o HTML do enunciado, colapsando múltiplos <br> e <p> vazios para somente um.
 */
function normalizeHTML(html: string): string {
  if (!html) return "";
  html = html.trim();
  html = html.replace(/(<br\s*\/?>\s*){2,}/gi, "<br>");
  html = html.replace(/(<p>\s*<\/p>\s*){2,}/gi, "<p></p>");
  return html;
}

/**
 * Cria o elemento combinado para a questão dissertativa.
 */
function createCombinedDissertativeElementSplit(
  fontSize: number,
  questionStatement: string,
  questionNumber: number,
  maxHeight: number,
  questionId?: string,
  isFinalPart: boolean = true
): string {
  // Verifica o espaço disponível com uma margem de segurança
  const hasEnoughSpace = maxHeight >= ANSWER_AREA_MIN_HEIGHT + 50;

  // Reduz o espaço máximo para o enunciado quando houver espaço para área de resposta
  const statementMaxHeight = hasEnoughSpace
    ? maxHeight - ANSWER_AREA_MIN_HEIGHT - 20 // 20px extra para margem
    : maxHeight;

  const normalizedHtml = normalizeHTML(questionStatement).trim();
  const container = document.createElement("div");
  container.innerHTML = normalizedHtml;
  const textContent = container.textContent || "";

  // Processa o conteúdo do enunciado
  const blocks = splitTextIntoBlocks(textContent, fontSize, statementMaxHeight);

  let formattingClone = `<div class="question-statement" style="font-size:${fontSize}px;">`;
  if (container.firstElementChild) {
    const firstEl = container.firstElementChild as HTMLElement;
    const inlineStyle = firstEl.getAttribute("style") || "";
    const classList = firstEl.getAttribute("class") || "question-statement";
    formattingClone = `<div class="${classList}" style="font-size:${fontSize}px; ${inlineStyle}">`;
  }

  const statementHtml = blocks
    .map((part) => `${formattingClone}${part}</div>`)
    .join("");

  const headerHtml = `<div class="question-header" style="margin-bottom:10px;font-size:${fontSize}px;">
    <strong>${questionNumber}.</strong>
  </div>`;

  // Enunciado com margem inferior adequada
  const wrappedStatementHtml = `<div class="question-statement-wrapper" style="margin-bottom:${
    hasEnoughSpace ? "20px" : "0"
  }">${statementHtml}</div>`;

  // Só cria a área de resposta se houver espaço suficiente
  let answerAreaHtml = "";
  if (hasEnoughSpace && isFinalPart && questionId) {
    answerAreaHtml = `<div class="answer-area-container" style="position: relative; width:100%;">
      <div class="answer-area pentagon-answer-box" style="min-height:${ANSWER_AREA_MIN_HEIGHT}px; border:1px solid #000;" data-element-type="dissertative" data-question-id="${questionId}" data-fixed="true">
        <div class="cornerMark cornerTopLeft" style="position:absolute; top:-7px; left:-7px; width:10px; height:10px; border-top:2px solid #000; border-left:2px solid #000;"></div>
        <div class="cornerMark cornerTopRight" style="position:absolute; top:-7px; right:-7px; width:10px; height:10px; border-top:2px solid #000; border-right:2px solid #000;"></div>
        <div class="cornerMark cornerBottomLeft" style="position:absolute; bottom:-7px; left:-7px; width:10px; height:10px; border-bottom:2px solid #000; border-left:2px solid #000;"></div>
        <div class="cornerMark cornerBottomRight" style="position:absolute; bottom:-7px; right:-7px; width:10px; height:10px; border-bottom:2px solid #000; border-right:2px solid #000;"></div>
      </div>
    </div>`;
  }

  return `<div class="question-container" style="padding:10px;">
    ${headerHtml}
    ${wrappedStatementHtml}
    ${answerAreaHtml}
  </div>`;
}

/**
 * Calcula as páginas a partir da prova selecionada.
 */
export function calculatePages({
  provaSelect = null,
  config = {} as PageCalculatorConfig,
  pageConfigs = {},
  pageColumns = [],
  setLoading = undefined,
}: {
  provaSelect?: QuestionDetails[] | null;
  config?: PageCalculatorConfig;
  pageConfigs?: Record<number, PageConfig>;
  pageColumns?: number[];
  setLoading?: (loading: boolean) => void;
} = {}): QuestionPage[] {
  if (!provaSelect?.length || !config) return [];

  const maxDissertativeHeight =
    config.effectiveHeight -
    (QR_CODE_HEIGHT + QR_CODE_MARGIN_BOTTOM + SEPARATOR_HEIGHT);

  try {
    const manager = new ContentManager(config, pageConfigs, pageColumns);
    const questions: QuestionDetails[] = config.randomQuestion
      ? shuffleArray(provaSelect)
      : provaSelect;

    questions.forEach((question, index) => {
      const hasContext =
        question.statement &&
        question.subQuestions &&
        question.subQuestions.length > 0;

      if (hasContext) {
        manager.addElement(
          `<div data-element-type="context">${question.statement}</div>`,
          config.fontSizeQuestoes,
          "context",
          question.id
        );
        question.subQuestions.forEach((subQ: any, subIndex: number) => {
          let processedSub = subQ.statement;
          if (
            /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(subQ.statement || "")
          ) {
            processedSub = processImgHTML(subQ.statement || "");
          }
          manager.addElement(
            `<div style="margin-left: ${
              config.marginEsquerda / 3
            }px; margin-right: ${config.marginDireita}px; margin-bottom: ${
              config.marginSuperior
            }px; margin-top: ${config.marginSuperior}px;">
              <strong>${subIndex + 1}.</strong> ${processedSub}
            </div>`,
            config.fontSizeQuestoes,
            "question",
            question.id
          );
          if (subQ.objectives && subQ.objectives.length > 0) {
            const objectives = config.randomAlternatives
              ? shuffleArray(subQ.objectives)
              : subQ.objectives;
            objectives.forEach((obj: any, altIndex: number) => {
              let processedObj = obj.text;
              if (
                /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(obj.text || "")
              ) {
                processedObj = processImgHTML(obj.text || "");
              }
              const letterLabel = String.fromCharCode(97 + altIndex);
              manager.addElement(
                `<div style="margin-left: ${config.marginEsquerda}px; margin-right: ${config.marginDireita}px; margin-bottom: ${config.marginSuperior}px; margin-top: ${config.marginSuperior}px;">
                  <strong>${letterLabel})</strong> ${processedObj}
                </div>`,
                config.fontSizeAlternatives,
                "alternative",
                question.id
              );
            });
          }
        });
      } else {
        let processedQuestionText = "";
        if (
          /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(
            question.statement || ""
          )
        ) {
          processedQuestionText = processImgHTML(question.statement || "");
        } else {
          const questionText =
            question.statement ||
            (question.subQuestions && question.subQuestions[0]
              ? question.subQuestions[0].statement
              : "");
          processedQuestionText =
            typeof questionText === "string"
              ? processImgHTML(questionText)
              : String(questionText || "");
        }
        if (!question.subQuestions?.[0]?.dissertative) {
          manager.addElement(
            `<div><strong>${
              index + 1
            }.</strong> ${processedQuestionText}</div>`,
            config.fontSizeQuestoes,
            "question",
            question.id
          );
        }
        const firstSub = question.subQuestions?.[0];
        if (firstSub) {
          if (firstSub.objectives && firstSub.objectives.length > 0) {
            const objectives = config.randomAlternatives
              ? shuffleArray(firstSub.objectives)
              : firstSub.objectives;
            objectives.forEach((obj: any, altIndex: number) => {
              let processedObj = obj.text;
              if (
                /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i.test(obj.text || "")
              ) {
                processedObj = processImgHTML(obj.text || "");
              }
              const letterLabel = String.fromCharCode(97 + altIndex);
              manager.addElement(
                `<div style="margin-left: ${config.marginEsquerda}px; margin-right: ${config.marginDireita}px; margin-bottom: ${config.marginSuperior}px; margin-top: ${config.marginSuperior}px;">
                  <strong>${letterLabel})</strong> ${processedObj}
                </div>`,
                config.fontSizeAlternatives,
                "alternative",
                question.id
              );
            });
          } else if (firstSub.dissertative) {
            // Para questões dissertativas, medimos o enunciado
            const tempDiv = document.createElement("div");
            tempDiv.style.fontSize = `${config.fontSizeQuestoes}px`;
            tempDiv.style.width = "calc(100% - 20px)";
            tempDiv.style.position = "absolute";
            tempDiv.style.visibility = "hidden";
            tempDiv.innerHTML = processedQuestionText;
            document.body.appendChild(tempDiv);
            const totalHeight = tempDiv.offsetHeight;
            document.body.removeChild(tempDiv);

            if (totalHeight > maxDissertativeHeight) {
              const midPoint = Math.floor(processedQuestionText.length / 2);
              let splitIndex = processedQuestionText.lastIndexOf(" ", midPoint);
              if (splitIndex < 0) splitIndex = midPoint;
              const firstPart = processedQuestionText
                .slice(0, splitIndex)
                .trim();
              const secondPart = processedQuestionText.slice(splitIndex).trim();

              manager.addElement(
                createCombinedDissertativeElementSplit(
                  config.fontSizeQuestoes,
                  firstPart,
                  index + 1,
                  maxDissertativeHeight,
                  question.id,
                  false
                ),
                config.fontSizeQuestoes,
                "dissertative",
                question.id
              );
              manager.addElement(
                createCombinedDissertativeElementSplit(
                  config.fontSizeQuestoes,
                  secondPart,
                  index + 1,
                  maxDissertativeHeight,
                  question.id,
                  true
                ),
                config.fontSizeQuestoes,
                "dissertative",
                question.id
              );
            } else {
              manager.addElement(
                createCombinedDissertativeElementSplit(
                  config.fontSizeQuestoes,
                  processedQuestionText,
                  index + 1,
                  maxDissertativeHeight,
                  question.id,
                  true
                ),
                config.fontSizeQuestoes,
                "dissertative",
                question.id
              );
            }
          }
        }
      }
      if (index < questions.length - 1) {
        manager.addElement(
          `<div id="separator" style="position: absolute; width: 100%; height: 1px; background-color: black; margin: 10px 0;"></div>`,
          config.fontSizeQuestoes,
          "separator"
        );
      }
    });

    const pages = manager.getPages();
    if (setLoading) setLoading(false);
    return pages;
  } catch (error) {
    console.error("Erro ao calcular páginas:", error);
    if (setLoading) setLoading(false);
    return [];
  }
}

export function recalculateFromPages({
  questionPages,
  config,
  pageConfigs = {},
  pageColumns = [],
  setLoading,
}: {
  questionPages?: QuestionPage[];
  config?: PageCalculatorConfig;
  pageConfigs?: Record<number, PageConfig>;
  pageColumns?: number[];
  setLoading?: (loading: boolean) => void;
} = {}): QuestionPage[] {
  if (!questionPages || questionPages.length === 0) return [];
  if (!config) {
    console.error("Missing config parameter in recalculateFromPages");
    return [];
  }

  // Em modo de edição ou impressão, apenas ajustamos tamanhos e posições, sem reprocessar HTML
  if (config.editMode || config.printMode) {

    // Clone profundo para evitar modificar o original
    const preservedPages = JSON.parse(JSON.stringify(questionPages));

    // Atualiza tamanhos de fonte mantendo a estrutura existente
    preservedPages.forEach((page: QuestionPage) => {
      page.elements.forEach((el: PageElement) => {
        const elementType = getElementType(el.element);
        const fontSize =
          elementType === "alternative"
            ? config.fontSizeAlternatives
            : config.fontSizeQuestoes;

        // Atualiza apenas o tamanho da fonte sem alterar estrutura
        if (el.element.includes('style="')) {
          el.element = el.element.replace(
            /font-size:\s*[^;"']+(?:px|%|em|rem|pt)/,
            `font-size: ${fontSize}px`
          );
        }

        // Não recalcule altura/posição para manter exatamente como estava
      });
    });

    if (setLoading) setLoading(false);
    return preservedPages;
  }

  // Caso contrário, reprocessamos os elementos normalmente (modo de criação)
  const manager = new ContentManager(config, pageConfigs, pageColumns);
  questionPages.forEach((page: QuestionPage) => {
    page.elements.forEach((el: PageElement) => {
      let appliedFontSize = config.fontSizeQuestoes;
      const elementType = getElementType(el.element);
      if (elementType === "alternative") {
        appliedFontSize = config.fontSizeAlternatives;
      }
      // Adiciona o elemento ao manager para que seja reprocessado e reposicionado
      manager.addElement(el.element, appliedFontSize, elementType);
    });
  });
  const newPages = manager.getPages();
  if (setLoading) setLoading(false);
  return newPages;
}

export function recalculatePageStyles({
  questionPages,
  config,
  setLoading,
}: {
  questionPages: QuestionPage[];
  config: PageCalculatorConfig;
  setLoading?: (loading: boolean) => void;
}): QuestionPage[] {
  if (!questionPages || questionPages.length === 0) return [];
  const newPages: QuestionPage[] = questionPages.map((page: QuestionPage) => {
    const columns = page.columns || config.numeroColunas;
    const newElements = page.elements.map((el: PageElement) => {
      let newHtml = el.element;
      const elementType = getElementType(newHtml);
      const appliedFontSize =
        elementType === "alternative"
          ? config.fontSizeAlternatives
          : config.fontSizeQuestoes;

      newHtml = newHtml
        .replace(
          /margin-left:\s*[^;"']+(?:px|%|em|rem)/,
          `margin-left: ${config.marginEsquerda}px`
        )
        .replace(
          /margin-right:\s*[^;"']+(?:px|%|em|rem)/,
          `margin-right: ${config.marginDireita}px`
        )
        .replace(
          /margin-bottom:\s*[^;"']+(?:px|%|em|rem)/,
          `margin-bottom: ${config.marginInferior}px`
        )
        .replace(
          /margin-top:\s*[^;"']+(?:px|%|em|rem)/,
          `margin-top: ${config.marginSuperior}px`
        )
        .replace(
          /font-size:\s*[^;"']+(?:px|%|em|rem|pt)/,
          `font-size: ${appliedFontSize}px`
        );

      if (!newHtml.includes('class="question-content"')) {
        newHtml = newHtml.replace(/<div/, '<div class="question-content"');
      }
      if (!newHtml.includes("data-element-type=")) {
        newHtml = newHtml.replace(
          /<div/,
          `<div data-element-type="${elementType}"`
        );
      }
      return {
        element: newHtml,
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
      };
    });
    return {
      elements: newElements,
      columns: columns,
    };
  });
  if (setLoading) setLoading(false);
  return newPages;
}

/**
 * Cria a configuração para o calculador de páginas.
 */
export function createPageCalculatorConfig({
  A4_WIDTH,
  A4_HEIGHT,
  numeroColunas,
  fontSizeQuestoes,
  fontSizeAlternatives,
  marginSuperior,
  marginInferior,
  marginEsquerda,
  marginDireita,
  randomAlternatives,
  randomQuestion,
  editMode,
  printMode,
}: {
  A4_WIDTH: number;
  A4_HEIGHT: number;
  numeroColunas: number;
  fontSizeQuestoes: number | "default";
  fontSizeAlternatives: number | "default";
  marginSuperior: number;
  marginInferior: number;
  marginEsquerda: number;
  marginDireita: number;
  randomAlternatives: boolean;
  randomQuestion: boolean;
  editMode?: boolean; 
  printMode?: boolean; // Modo de impressão
  // Parâmetros opcionais para o QR Code:
  studentId?: string;
  registrationId?: string;
  testId?: string;
  answerSheetId?: string;
  diagramId?: string;
  urltoken?: string; // Novo parâmetro para URL dinâmica
}): PageCalculatorConfig {
  const appliedFontSizeQuestoes =
    typeof fontSizeQuestoes === "number"
      ? fontSizeQuestoes
      : fontSizeQuestoes === "default"
      ? 13
      : 13;
  const appliedFontSizeAlternatives =
    typeof fontSizeAlternatives === "number"
      ? fontSizeAlternatives
      : fontSizeAlternatives === "default"
      ? 13
      : 13;
  return {
    A4_WIDTH,
    A4_HEIGHT,
    effectiveHeight: A4_HEIGHT - (marginSuperior + marginInferior),
    effectiveWidth: A4_WIDTH - (marginEsquerda + marginDireita),
    fontSizeQuestoes: appliedFontSizeQuestoes,
    fontSizeAlternatives: appliedFontSizeAlternatives,
    marginSuperior,
    marginInferior,
    marginEsquerda,
    marginDireita,
    numeroColunas,
    randomAlternatives,
    randomQuestion,
    editMode, // seta a flag para o modo de edição
    printMode,
  };
}


